/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Skeleton } from "@material-ui/lab";
import { useProjectsOT } from "src/hooks/redux/useProjectsOT";
import { getProjectChecklistsWithResponses } from "src/redux/actions/projectsActions";
import { useDispatch } from "react-redux";
import { SheetOT } from "src/models/OT/Sheet";
import { otActions } from "src/redux/neo-checklists/slice";
import { AssignedPlaceOT } from "src/models/OT/Place";
import { ChecklistOT } from "src/models/OT/Checklist";
import { useCheckproLinks } from "src/components/CheckPro/Links/hooks/useCheckproLinks";

// Definir interfaces para la respuesta
interface PlaceWithResponses {
	place: AssignedPlaceOT;
	responses: SheetOT[];
	cantidad: number;
}

interface ChecklistWithResponses {
	checklist: ChecklistOT;
	placesWithResponses: PlaceWithResponses[];
}

// Primero, definir una interfaz para los totales
interface ChecklistTotal {
	name: string;
	totalProjected: number;
	totalCompleted: number;
	totalPending: number;
}

export const RevisionGoalGraphOT = () => {
	const { checklist, project, selected } = useProjectsOT();
	const { idEmpresa } = useCheckproLinks();
	// @ts-ignore
	const dispatch = useDispatch<AppDispatch>();
	const [pendingSheets, setPendingSheets] = useState<ChecklistTotal[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasLoaded, setHasLoaded] = useState(false);

	// Primer useEffect para debug
	useEffect(() => {
		console.log('Estado actual:', {
			hasProject: !!selected.project,
				projectId: selected.project?._ref?.id,
				isLoading
		});
	}, [selected.project, isLoading]);

	useEffect(() => {
		const fetchData = async () => {
			if (!selected.project?._ref?.id || hasLoaded) {
				console.log('No hay proyecto seleccionado o ya se cargaron los datos');
				return;
			}

			if (isLoading) {
				console.log('Ya hay una carga en proceso');
				return;
			}

			try {
				setIsLoading(true);
				console.log('1. Iniciando obtención de datos para proyecto:', selected.project._ref.id);

				const checklistsData = await dispatch(
					getProjectChecklistsWithResponses(selected.project._ref.id, idEmpresa)
				) as unknown as ChecklistWithResponses[];

				console.log('2. Datos obtenidos:', checklistsData);

				// Procesar todas las respuestas
				let sheets: SheetOT[] = [];
				let inProcessSheets: ChecklistTotal[] = [];

				checklistsData.forEach(({ placesWithResponses, checklist }) => {
					// Filtrar solo los espacios asignados (Asignado !== false)
					const assignedPlaces = placesWithResponses.filter(
						({ place }) => (place as any).Asignado !== false
					);

					// 1. Calcular total proyectado sumando cada piso asignado (EspacioAsignadoOT)
					const totalProjected = assignedPlaces.reduce((acc, { place }) => acc + (place.Cantidad || 0), 0);
					
					// 2. Calcular total completado sumando cada piso asignado
					const totalCompleted = assignedPlaces.reduce((acc, { place }) => acc + (place.CantidadCompletada || 0), 0);

					// 3. Calcular total PENDIENTE según regla:
					// "Sumar la cantidad de cuadrantes (Unidades) de cada respuesta pendiente"
					// "Dividir esa suma entre el total proyectado = % Pendiente"
					let totalPendingUnidades = 0;

					// Log detallado por piso
					const floorDetails: any[] = [];

					assignedPlaces.forEach(({ place, responses }) => {
						let floorPendingUnidades = 0;

						responses.forEach((response) => {
							// Una respuesta es pendiente si su Estado es "En Proceso"
							if (response.Estado === "En Proceso") {
								const unidades = (response as any).Unidades || 0;
								floorPendingUnidades += typeof unidades === 'number' ? unidades : 0;
							}
						});

						totalPendingUnidades += floorPendingUnidades;

						floorDetails.push({
							Piso: place.Nombre || '(sin nombre)',
							Cantidad: place.Cantidad || 0,
							Completada: place.CantidadCompletada || 0,
							"Respuestas Totales": responses.length,
							"Resp. En Proceso": responses.filter(r => r.Estado === "En Proceso").length,
							"Cuadrantes Pendientes": floorPendingUnidades
						});

						// Acumular hojas de respuestas
						sheets = [...sheets, ...responses];
					});

					console.log(`%c[RevisionGoal] Checklist: ${checklist.Nombre}`, "color: #ff9800; font-weight: bold;");
					console.table(floorDetails);
					console.log(`[RevisionGoal] RESUMEN ${checklist.Nombre} => Proyectado: ${totalProjected} | Completado: ${totalCompleted} | Cuadrantes Pendientes: ${totalPendingUnidades} | %Pendiente: ${totalProjected > 0 ? Math.round((totalPendingUnidades / totalProjected) * 100) : 0}%`);

					// Guardar los totales para este checklist
					inProcessSheets.push({
						name: checklist.Nombre || '',
						totalProjected,
						totalCompleted,
						totalPending: totalPendingUnidades
					});
				});

				console.log('3. Total hojas procesadas:', sheets.length);
				console.log('4. Hojas en proceso:', inProcessSheets.length);


				setPendingSheets(inProcessSheets);
				setHasLoaded(true);

			} catch (error) {
				console.error('Error en el proceso:', error);
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	}, [selected.project?._ref?.id, hasLoaded]);

	// Reset hasLoaded cuando cambia el proyecto
	useEffect(() => {
		setHasLoaded(false);
	}, [selected.project?._ref?.id]);

	const goals = useMemo(
		() => project.checklists?.map((p) => p.MetaRevision ?? 0) ?? [],
		[project.checklists]
	);

	const { realValues, pendingGoals, chartLabels } = useMemo(() => {
		const labels: string[] = [];
		const real: number[] = [];
		const pending: number[] = [];

		project.checklists?.forEach(checklist => {
			const label = checklist.Nombre || '';
			labels.push(label);

			const checklistData = pendingSheets.find(sheet => sheet.name === label);
			
			if (!checklistData || checklistData.totalProjected === 0) {
				real.push(0);
				pending.push(0);
			} else {
				// Real (%) = Completado / Proyectado * 100
				const realVal = Math.round((checklistData.totalCompleted / checklistData.totalProjected) * 100);
				real.push(realVal);

				// Pendiente (%) = (Items clasificados como pendientes) / Proyectado * 100
				const pendingVal = Math.round((checklistData.totalPending / checklistData.totalProjected) * 100);
				pending.push(pendingVal);
			}
		});

		return { chartLabels: labels, realValues: real, pendingGoals: pending };
	}, [pendingSheets, project.checklists]);

	// Nuevo console.table para depuración de datos del gráfico
	useEffect(() => {
		if (chartLabels.length > 0) {
			const tableData = chartLabels.map((label, index) => {
				const checklistData = pendingSheets.find(s => s.name === label);
				return {
					Checklist: label,
					"Meta (%)": goals[index],
					"Real (%)": realValues[index],
					"Pendiente (%)": pendingGoals[index],
					"Proyectado (Total)": checklistData?.totalProjected || 0,
					"Completadas": checklistData?.totalCompleted || 0,
					"Pendientes (Suma de Gaps)": checklistData?.totalPending || 0
				};
			});
			
			console.log("%c--- Datos del Gráfico de Metas (Lógica de Gaps sobre Pendientes) ---", "color: #2196f3; font-weight: bold; font-size: 14px;");
			console.table(tableData);
		}
	}, [chartLabels, goals, realValues, pendingGoals, pendingSheets]);

	// Agregar un useEffect para monitorear los cambios en las dependencias
	useEffect(() => {
		console.log('Cambió goals:', goals);
		console.log('Cambió realValues:', realValues);
	}, [ goals, realValues]);

	const max = useMemo(() => {
		const values = [
			...realValues,
			...goals,
			...pendingGoals,
		];

		return Math.max(...values) * 1.15;
	}, [checklist.graphData, goals, pendingGoals]);

	if (!checklist.graphData)
		return <Skeleton variant="rect" height={400} />;

	console.log('Pendientes finales:', pendingGoals);

	return (
		<Bar
			options={{
				legend: {
					display: true,
				},
				scales: {
					xAxes: [{
						stacked: false,
						categoryPercentage: 0.8,
						barPercentage: 0.9,
					}],
					yAxes: [
						{
							stacked: false,
							ticks: {
								beginAtZero: true,
								max,
							},
						},
					],
				},
				plugins: {
					labels: [
						{
							render: (col: any) => {
								// Solo mostrar valor si es mayor a 0
								if (col.value <= 0) return '';


								return `${col.value}%`;
							},
							position: "outside",
							precision: 2,
						},
					],
				},
			}}
			data={{
				labels: chartLabels,
				datasets: [
					{
						label: "Meta",
						data: goals,
						backgroundColor: "#396CA2"
					},
					{
						label: "Real",
						data: realValues,
						backgroundColor: "#00B050"
					},
					{
						label: "Pendiente",
						data: pendingGoals,
						backgroundColor: "yellow"
					}
				],
			}}
		/>
	);
};
